/**
 * Image editor: zoom, crop, rotate, flip, reposition, aspect-ratio lock and a
 * live preview of exactly what will be saved.
 *
 * The interaction surface is `react-easy-crop` (small, no dependencies beyond
 * `normalize-wheel`, actively maintained). Everything it gives us is used:
 * `zoom` + wheel/pinch, drag-to-reposition, `rotation`, and `aspect` for the
 * ratio lock. Flip is ours — see `imageEditing.ts` for why it can ride along in
 * the same export pass without disturbing the cropper's coordinate maths.
 *
 * Two ways out:
 *   * **Replace the original** — same asset id, so every insurer card, product
 *     tile and microsite section already pointing at this image updates. This
 *     is the destructive one and is called out as such.
 *   * **Save as a new image** — uploads a second asset and leaves the original
 *     untouched.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Cropper, { type Area } from 'react-easy-crop';
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  ArrowsRightLeftIcon,
  ArrowsUpDownIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { Button, Input } from '@/components/common';
import { cn } from '@/utils';
import {
  assetsService,
  formatBytes,
  fullSrc,
  type AssetConfig,
  type MediaAsset,
} from '@/services/assets';
import { getErrorMessage } from '@/services/api';
import { assetKeys } from '@/hooks/useAssets';

import { MediaDialog } from './MediaDialog';
import { CHECKERBOARD } from './AssetThumb';
import {
  ASPECT_PRESETS,
  MAX_ZOOM,
  MIN_ZOOM,
  clamp,
  editedFilename,
  fitOutputSize,
  isIdentityEdit,
  loadImageElement,
  normalizeRotation,
  outputFormatFor,
  renderEditedImage,
  renderedToFile,
  rotateSize,
  type CropArea,
  type EditTransform,
} from './imageEditing';

interface ImageEditorModalProps {
  asset: MediaAsset;
  isOpen: boolean;
  onClose: () => void;
  config?: AssetConfig | null;
  /** Called with the resulting asset — the replaced one, or the new one. */
  onSaved?: (asset: MediaAsset, mode: 'replace' | 'copy') => void;
}

type SaveMode = 'replace' | 'copy';

export function ImageEditorModal({ asset, isOpen, onClose, config, onSaved }: ImageEditorModalProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flipHorizontal, setFlipHorizontal] = useState(false);
  const [flipVertical, setFlipVertical] = useState(false);
  const [aspectIndex, setAspectIndex] = useState(0);
  const [customRatio, setCustomRatio] = useState<{ w: string; h: string }>({ w: '', h: '' });
  const [croppedArea, setCroppedArea] = useState<CropArea | null>(null);
  const [outputWidth, setOutputWidth] = useState<string>('');
  const [saving, setSaving] = useState<SaveMode | null>(null);

  const queryClient = useQueryClient();
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const source = fullSrc(asset);
  const isVector = asset.render_hint === 'img-only';

  const transform: EditTransform = useMemo(
    () => ({ rotation, flipHorizontal, flipVertical }),
    [rotation, flipHorizontal, flipVertical],
  );

  const reset = useCallback(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setFlipHorizontal(false);
    setFlipVertical(false);
    setAspectIndex(0);
    setCustomRatio({ w: '', h: '' });
    setOutputWidth('');
  }, []);

  // Load the pixels once per open. Same-origin, so the canvas is never tainted.
  useEffect(() => {
    if (!isOpen || isVector) return;
    let cancelled = false;
    setLoadError(null);
    setImage(null);
    loadImageElement(source)
      .then((loaded) => {
        if (!cancelled) setImage(loaded);
      })
      .catch((error: Error) => {
        if (!cancelled) setLoadError(error.message);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, source, isVector]);

  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen, reset]);

  const sourceSize = useMemo(
    () => ({
      width: image?.naturalWidth || asset.width || 0,
      height: image?.naturalHeight || asset.height || 0,
    }),
    [image, asset.width, asset.height],
  );

  const nativeAspect = sourceSize.width && sourceSize.height ? sourceSize.width / sourceSize.height : 1;

  const customAspect = useMemo(() => {
    const w = Number(customRatio.w);
    const h = Number(customRatio.h);
    if (w > 0 && h > 0) return w / h;
    return null;
  }, [customRatio]);

  const aspect = customAspect ?? ASPECT_PRESETS[aspectIndex]?.value ?? nativeAspect;

  // --- live preview --------------------------------------------------------
  // Debounced: a drag fires onCropComplete continuously and each render is a
  // full-size canvas draw plus a PNG encode.
  const previewToken = useRef(0);
  // The live object URL is mirrored in a ref: React does not run state updaters
  // for an unmounted component, so a `setPreviewUrl` in the unmount cleanup
  // would never fire and the last blob would leak.
  const previewUrlRef = useRef<string | null>(null);
  useEffect(() => {
    if (!image || !croppedArea) return;
    const token = ++previewToken.current;
    const timer = setTimeout(() => {
      renderEditedImage(image, croppedArea, transform, {
        // Small: this is a thumbnail of the result, not the result.
        maxWidth: 360,
        mime: 'image/png',
      })
        .then((rendered) => {
          if (token !== previewToken.current) return;
          if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
          previewUrlRef.current = URL.createObjectURL(rendered.blob);
          setPreviewUrl(previewUrlRef.current);
        })
        .catch(() => {
          /* the preview is a nicety; a failure here must not break the editor */
        });
    }, 220);
    return () => clearTimeout(timer);
  }, [image, croppedArea, transform]);

  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    },
    [],
  );

  // The size the saved file will actually be, after the crop and any resize.
  const finalSize = useMemo(() => {
    if (!croppedArea) return null;
    const cap = Number(outputWidth);
    return fitOutputSize(
      { width: croppedArea.width, height: croppedArea.height },
      Number.isFinite(cap) && cap > 0 ? cap : null,
      config?.max_pixels ?? null,
    );
  }, [croppedArea, outputWidth, config?.max_pixels]);

  const format = outputFormatFor(asset.extension);
  const untouched = isIdentityEdit(
    croppedArea,
    transform,
    sourceSize,
    Number(outputWidth) || null,
  );

  const rotateBy = (degrees: number) => setRotation((current) => normalizeRotation(current + degrees));

  const buildFile = useCallback(async () => {
    if (!image || !croppedArea) throw new Error('Nothing to save yet.');
    const cap = Number(outputWidth);
    const rendered = await renderEditedImage(image, croppedArea, transform, {
      maxWidth: Number.isFinite(cap) && cap > 0 ? cap : null,
      maxPixels: config?.max_pixels ?? null,
      mime: format.mime,
      quality: 0.92,
    });
    return renderedToFile(rendered, editedFilename(asset.original_filename, format.extension));
  }, [image, croppedArea, transform, outputWidth, config?.max_pixels, format, asset.original_filename]);

  const save = async (mode: SaveMode) => {
    setSaving(mode);
    try {
      const file = await buildFile();
      if (mode === 'replace') {
        const replaced = await assetsService.replace(asset.id, {
          file,
          // Only needed when the canvas could not write the stored format
          // (GIF/AVIF/ICO). Sending it otherwise would let an accidental format
          // switch move the direct URL for no reason.
          allow_format_change: format.formatChanged,
        });
        toast.success(
          replaced.url_changed
            ? 'Saved. The file format changed, so the direct URL moved — permanent links still work.'
            : 'Saved. Every page using this image now shows the edit.',
        );
        onSaved?.(replaced, 'replace');
      } else {
        const created = await assetsService.upload({
          file,
          folder: asset.folder,
          // Carry the SEO fields over: a crop of the same picture describes the
          // same thing, and a blank alt text on the copy would be a regression.
          alt_text: asset.alt_text,
          title: asset.title ? `${asset.title} (edited)` : '',
          caption: asset.caption,
          description: asset.description,
          tags: asset.tags,
        });
        toast.success('Saved as a new image. The original is untouched.');
        onSaved?.(created, 'copy');
      }
      // The editor calls the service directly rather than going through the
      // mutation hooks, so nothing has invalidated the caches yet — without
      // this the grid, the counters and the folder counts stay stale.
      queryClient.invalidateQueries({ queryKey: assetKeys.all });
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(null);
    }
  };

  const bboxLabel = useMemo(() => {
    if (!sourceSize.width) return '';
    const box = rotateSize(sourceSize.width, sourceSize.height, rotation);
    return `${Math.round(box.width)} × ${Math.round(box.height)}`;
  }, [sourceSize, rotation]);

  return (
    <MediaDialog
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      size="7xl"
      title="Edit image"
      description={asset.original_filename}
      closeOnOverlayClick={!saving}
      footer={
        <>
          <span className="mr-auto text-xs text-gray-500 dark:text-gray-400">
            {finalSize
              ? `Saves at ${finalSize.width} × ${finalSize.height} as ${format.extension.toUpperCase()}`
              : 'Adjust the crop to see the output size'}
          </span>
          <Button variant="outline" onClick={onClose} disabled={Boolean(saving)}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={() => save('copy')}
            isLoading={saving === 'copy'}
            disabled={Boolean(saving) || !image || untouched}
          >
            Save as new image
          </Button>
          <Button
            variant="primary"
            onClick={() => save('replace')}
            isLoading={saving === 'replace'}
            disabled={Boolean(saving) || !image || untouched || !asset.can_edit}
          >
            Replace original
          </Button>
        </>
      }
    >
      {isVector ? (
        <EditorNotice
          tone="warning"
          title="Vectors are not edited here"
          body="This is an SVG. Cropping it would turn it into a fixed-size bitmap and lose the reason to use a vector in the first place. Edit it in your drawing tool and use Replace file to upload the new version."
        />
      ) : loadError ? (
        <EditorNotice tone="error" title="Could not load the image" body={loadError} />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          {/* --- canvas --- */}
          <div className="space-y-3">
            <div className="relative h-[420px] overflow-hidden rounded-xl bg-gray-900">
              {image ? (
                <Cropper
                  image={source}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={aspect}
                  minZoom={MIN_ZOOM}
                  maxZoom={MAX_ZOOM}
                  // Left at the default `true` on purpose: with it off, the
                  // crop box can be dragged past the edge of the image and the
                  // reported rect goes out of bounds, so the saved file would
                  // silently come back a different size from the preview.
                  showGrid
                  objectFit="contain"
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onRotationChange={setRotation}
                  onCropComplete={(_area: Area, pixels: Area) => setCroppedArea(pixels)}
                  // The library's own transform, with the flips appended. They
                  // are the innermost operation, so the translate/rotate/scale
                  // chain its crop maths depends on is unchanged.
                  transform={[
                    `translate(${crop.x}px, ${crop.y}px)`,
                    `rotate(${rotation}deg)`,
                    `scale(${zoom})`,
                    `scaleX(${flipHorizontal ? -1 : 1})`,
                    `scaleY(${flipVertical ? -1 : 1})`,
                  ].join(' ')}
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-gray-400">
                  Loading image…
                </div>
              )}
            </div>

            {/* zoom */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setZoom((z) => clamp(Number((z - 0.1).toFixed(2)), MIN_ZOOM, MAX_ZOOM))}
                className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                aria-label="Zoom out"
              >
                <MagnifyingGlassMinusIcon className="h-4 w-4" />
              </button>
              <input
                type="range"
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={0.01}
                value={zoom}
                onChange={(event) => setZoom(Number(event.target.value))}
                className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-gray-200 accent-primary-600 dark:bg-gray-700"
                aria-label="Zoom"
              />
              <button
                type="button"
                onClick={() => setZoom((z) => clamp(Number((z + 0.1).toFixed(2)), MIN_ZOOM, MAX_ZOOM))}
                className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                aria-label="Zoom in"
              >
                <MagnifyingGlassPlusIcon className="h-4 w-4" />
              </button>
              <span className="w-12 shrink-0 text-right text-xs tabular-nums text-gray-500 dark:text-gray-400">
                {Math.round(zoom * 100)}%
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Drag the image to reposition it. Scroll or pinch to zoom.
            </p>
          </div>

          {/* --- controls --- */}
          <div className="space-y-5">
            <ControlBlock label="Rotate">
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => rotateBy(-90)} leftIcon={<ArrowUturnLeftIcon className="h-4 w-4" />}>
                  90° left
                </Button>
                <Button variant="outline" size="sm" onClick={() => rotateBy(90)} leftIcon={<ArrowPathIcon className="h-4 w-4" />}>
                  90° right
                </Button>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <input
                  type="range"
                  min={0}
                  max={359}
                  step={1}
                  value={rotation}
                  onChange={(event) => setRotation(Number(event.target.value))}
                  className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-gray-200 accent-primary-600 dark:bg-gray-700"
                  aria-label="Fine rotation"
                />
                <span className="w-10 shrink-0 text-right text-xs tabular-nums text-gray-500 dark:text-gray-400">
                  {Math.round(rotation)}°
                </span>
              </div>
              {rotation % 90 !== 0 && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  A free angle grows the canvas to {bboxLabel} before cropping.
                </p>
              )}
            </ControlBlock>

            <ControlBlock label="Flip">
              <div className="flex flex-wrap gap-2">
                <ToggleButton
                  active={flipHorizontal}
                  onClick={() => setFlipHorizontal((value) => !value)}
                  icon={<ArrowsRightLeftIcon className="h-4 w-4" />}
                  label="Horizontal"
                />
                <ToggleButton
                  active={flipVertical}
                  onClick={() => setFlipVertical((value) => !value)}
                  icon={<ArrowsUpDownIcon className="h-4 w-4" />}
                  label="Vertical"
                />
              </div>
            </ControlBlock>

            <ControlBlock label="Aspect ratio">
              <div className="flex flex-wrap gap-1.5">
                {ASPECT_PRESETS.map((preset, index) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => {
                      setAspectIndex(index);
                      setCustomRatio({ w: '', h: '' });
                    }}
                    className={cn(
                      'rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors',
                      !customAspect && aspectIndex === index
                        ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700',
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  placeholder="W"
                  value={customRatio.w}
                  onChange={(event) => setCustomRatio((r) => ({ ...r, w: event.target.value }))}
                  className="dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                  aria-label="Custom ratio width"
                />
                <span className="text-gray-400">:</span>
                <Input
                  type="number"
                  min={1}
                  placeholder="H"
                  value={customRatio.h}
                  onChange={(event) => setCustomRatio((r) => ({ ...r, h: event.target.value }))}
                  className="dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                  aria-label="Custom ratio height"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                The crop box is locked to {aspect.toFixed(3)}:1. Fill both boxes for a custom lock.
              </p>
            </ControlBlock>

            <ControlBlock label="Output width">
              <Input
                type="number"
                min={1}
                placeholder={`${croppedArea?.width || sourceSize.width || ''} (no resize)`}
                value={outputWidth}
                onChange={(event) => setOutputWidth(event.target.value)}
                className="dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                aria-label="Output width in pixels"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Leave empty to keep the cropped pixels. The editor never upscales.
              </p>
            </ControlBlock>

            <ControlBlock label="Preview">
              <div
                className="flex min-h-[110px] items-center justify-center overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
                style={{ background: asset.has_alpha ? CHECKERBOARD : '#f8fafc' }}
              >
                {previewUrl ? (
                  <img src={previewUrl} alt="Preview of the edited image" className="max-h-40 w-auto" />
                ) : (
                  <span className="p-4 text-xs text-gray-400">Adjusting…</span>
                )}
              </div>
              <dl className="mt-2 space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                <div className="flex justify-between">
                  <dt>Original</dt>
                  <dd>
                    {asset.width} × {asset.height} · {formatBytes(asset.bytes)}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt>After edit</dt>
                  <dd>{finalSize ? `${finalSize.width} × ${finalSize.height}` : '—'}</dd>
                </div>
              </dl>
            </ControlBlock>

            <div className="flex items-center justify-between border-t border-gray-100 pt-3 dark:border-gray-700">
              <button
                type="button"
                onClick={reset}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              >
                Reset all changes
              </button>
              {asset.is_animated && (
                <span className="text-xs text-amber-600 dark:text-amber-400">Animation will be flattened</span>
              )}
            </div>

            {format.formatChanged && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                A browser cannot write {asset.extension.toUpperCase()}, so the edit is saved as PNG. If you
                replace the original, its direct file URL changes — permanent links keep working.
              </p>
            )}
            {!asset.can_edit && (
              <p className="rounded-lg bg-gray-100 px-3 py-2 text-xs text-gray-600 dark:bg-gray-900/60 dark:text-gray-300">
                This image belongs to another library, so it cannot be replaced. You can still save your
                edit as a new image in your own library.
              </p>
            )}
          </div>
        </div>
      )}
    </MediaDialog>
  );
}

function ControlBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </p>
      {children}
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
        active
          ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
          : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function EditorNotice({
  tone,
  title,
  body,
}: {
  tone: 'warning' | 'error';
  title: string;
  body: string;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border px-4 py-3 text-sm',
        tone === 'warning'
          ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300'
          : 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300',
      )}
    >
      <p className="font-semibold">{title}</p>
      <p className="mt-1">{body}</p>
    </div>
  );
}
